// ---------------------------------------------------------------------------
// Renderer: Text Block
//
// Renders a block of text slots with optional card-style layout (cards mode).
// Supports slot-level styling (italic, bold, serif, center), markdown inline
// formatting, max_lines truncation, and card expansion for slack fill.
// ---------------------------------------------------------------------------

import Konva from "konva";
import CustomText from "../CustomText";
import { themeColor, defaultTheme } from "../renderers/plainObjectRenderer";
import { formatValue } from "../shared/formatting.js";

// Cross-renderer helpers — imported from VizDslRenderer.js during migration.
// These will be extracted to ui/src/renderers/helpers.js in a later pass.
import { tagEvidence, addDebugOverlay } from "./helpers.js";

export function renderTextBlock(block, crystalizedData, options) {
  const { x = 0, y = 0, width = 300, height, imageCallback, theme, fontScale = 1, _measureOnly } = options;
  const s = fontScale;
  const themeColors = theme || defaultTheme;
  const group = new Konva.Group({ x, y, width });

  let bgRect = null;
  const data = crystalizedData[block.id] || {};
  const slots = block.slots || [];
  // block.padding is either a number (legacy — same inset on all 4 sides,
  // with the historical quirk that TOP is 0 unless there's a background) or
  // an object { left, right, top, bottom } for explicit per-side control —
  // e.g. to left-align a header with a sibling block's content without also
  // inheriting its bottom margin. Object form's `top` always applies,
  // background or not — it's an explicit author choice, not the legacy default.
  const padRaw = block.padding ?? 12;
  const padIsObject = padRaw !== null && typeof padRaw === "object";
  const padLeft = Math.round((padIsObject ? (padRaw.left ?? 12) : padRaw) * s);
  const padRight = Math.round((padIsObject ? (padRaw.right ?? 12) : padRaw) * s);
  const padTop = Math.round((padIsObject ? (padRaw.top ?? 12) : padRaw) * s);
  const padBottom = Math.round((padIsObject ? (padRaw.bottom ?? 12) : padRaw) * s);
  const padding = padLeft; // legacy alias — every other usage below is a LEFT-side or symmetric-by-construction inset
  // Optional top margin: rendered as empty space above the card so the slide
  // surface shows through. It's baked into the block's final height, so a
  // content-sized block in a tight vertical flow pushes its siblings down.
  const marginTop = Math.round((block.margin_top || 0) * s);
  if (block.background) {
    bgRect = new Konva.Rect({ y: marginTop, width, height: 0, fill: themeColor(themeColors, block.background, block.background), cornerRadius: Math.round((block.corner_radius ?? 4) * s) });
    group.add(bgRect);
  }

  // Cards (blocks with a visible background) get symmetric top/bottom padding.
  let cy = marginTop + (padIsObject ? padTop : (block.background ? padding : 0));
  const contentWidth = width - padLeft - padRight;
  const cardsMode = block.slot_layout === "cards";
  const cardRefs = [];

  // Label
  if (block.label) {
    const labelText = new CustomText({
      x: padding, y: cy, width: contentWidth,
      text: block.label.toUpperCase(),
      fontSize: Math.round((block.label_size ?? 11) * s), fontStyle: "bold",
      fontFamily: block.font_family || "Arial, sans-serif",
      letterSpacing: Math.round((block.label_spacing ?? 0) * s),
      fill: block.label_color
        ? themeColor(themeColors, block.label_color, block.label_color)
        : themeColor(themeColors, "textSecondary", "#666"),
      lineHeight: 1.3, refreshCallback: imageCallback
    });
    group.add(labelText);
    cy += labelText.height() + Math.round(6 * s);
  }

  // Block-level accent bar (skipped in cards mode)
  let accentBarRect = null;
  if (block.accent_bar && !cardsMode) {
    const ab = block.accent_bar;
    accentBarRect = new Konva.Rect({ x: 0, y: marginTop, width: Math.round((ab.width || 3) * s), height: 0, fill: themeColor(themeColors, ab.color || "accent", ab.color || "#1F3A2E") });
    group.add(accentBarRect);
  }

  const makeSlotText = (valueStr, fontSize, styleConfig, hasMarkdown, maxLines, isPreviewSlot) => {
    return new CustomText({
      x: padding, y: cy, width: contentWidth,
      text: valueStr, fontSize, lineHeight: 1.35,
      fill: themeColor(themeColors, "text", "#1a1a1a"),
      ...styleConfig,
      withMarkdown: hasMarkdown,
      _preview: isPreviewSlot,
      refreshCallback: imageCallback
    });
  };

  // Preview mode with no data: draw skeleton bars filling the content area.
  if (options._preview && (!data || Object.keys(data).length === 0)) {
    const slotCount = Math.max(1, slots.length);
    const barGap = Math.round(6 * s);
    const barH = Math.round(10 * s);
    const SKEL = "#e8e8e8";  // single consistent skeleton color

    if (block.label) {
      cy += Math.round(4 * s);  // label already rendered above, just add gap
    } else {
      cy = marginTop + Math.round(8 * s);   // no label — give top breathing room
    }

    if (cardsMode) {
      const cardPad = Math.round(14 * s);
      const accentW = Math.round(3 * s);
      const cardBg = themeColor(themeColors, block.card_background || "panel", block.card_background || "#F5F1E8");
      const accentColor = themeColor(themeColors, block.accent_bar?.color || "accent", block.accent_bar?.color || "#1F3A2E");
      const innerW = contentWidth - cardPad * 2 - accentW - padding;

      for (let i = 0; i < Math.min(slotCount, 3); i++) {
        const cardH = Math.round(60 * s);
        group.add(new Konva.Rect({
          x: padding, y: cy, width: contentWidth, height: cardH,
          fill: cardBg, cornerRadius: Math.round(4 * s)
        }));
        group.add(new Konva.Rect({
          x: padding, y: cy, width: accentW, height: cardH,
          fill: accentColor
        }));
        const tx = padding + accentW + cardPad;
        const tw = innerW;
        const lineHeights = [barH, barH * 0.8, barH * 0.6];
        let lineY = cy + cardPad;
        for (const lh of lineHeights) {
          const lw = tw * (0.95 - (lineY - cy - cardPad) / cardH * 0.4);
          group.add(new Konva.Rect({
            x: tx, y: lineY, width: lw, height: lh,
            fill: SKEL, cornerRadius: Math.round(2 * s)
          }));
          lineY += barH + barGap;
        }
        cy += cardH + Math.round(8 * s);
      }
    } else if (block.accent_bar) {
      const accentW = Math.round((block.accent_bar.width || 3) * s);
      const leftPad = padding + accentW + Math.round(10 * s);
      const usableW = width - leftPad - padding;
      for (let i = 0; i < Math.min(slotCount, 3); i++) {
        group.add(new Konva.Rect({
          x: leftPad, y: cy, width: usableW * (0.45 - i * 0.08),
          height: barH, fill: SKEL, cornerRadius: Math.round(2 * s)
        }));
        cy += barH + barGap;
        const bodyCount = 2 + (i % 2);
        for (let b = 0; b < bodyCount; b++) {
          group.add(new Konva.Rect({
            x: leftPad, y: cy, width: usableW * (0.90 - b * 0.12 - i * 0.05),
            height: Math.round(7 * s), fill: SKEL, cornerRadius: Math.round(2 * s)
          }));
          cy += Math.round(9 * s);
        }
        cy += barGap * 2;
      }
    } else {
      for (let i = 0; i < Math.min(slotCount * 2, 6); i++) {
        const widths = [0.92, 0.78, 0.55, 0.85, 0.63, 0.44];
        const w = (width - padding * 2) * widths[i % widths.length];
        group.add(new Konva.Rect({
          x: padding, y: cy, width: w, height: barH,
          fill: SKEL, cornerRadius: Math.round(2 * s)
        }));
        cy += barH + barGap;
      }
    }

    const finalH = cy + padding;
    group.height(finalH);
    if (bgRect) bgRect.height(finalH - marginTop);
    if (accentBarRect) accentBarRect.height(finalH - marginTop);
    addDebugOverlay(group, block, { ...options, height: finalH });
    return group;
  }

  for (let si = 0; si < slots.length; si++) {
    const slot = slots[si];
    let value;
    if (slot.role === "literal") {
      value = slot.value;
    } else if (slot.role === "llm_gen" || slot.role === "extract") {
      value = data[slot.key];
      if (slot.format && value != null) value = formatValue(value, slot.format);
      if (slot.label && value != null) value = `${slot.label}: ${value}`;
    }
    const isPreview = options._preview;
    if (value == null || (typeof value === "string" && value.trim() === "")) {
      if (!isPreview) continue;
      // Preview mode: render skeleton bars matching the slot's intended size.
      // Use a placeholder text with enough newlines to generate ~3-5 lines of skeleton bars.
      const placeholderWords = Math.max(1, Math.floor((slot.max_words || 30) / 6));
      value = "line\n".repeat(Math.min(placeholderWords, 5)).trim() || "line";
      // Mark this slot for skeleton rendering — the node gets _preview after creation.
    }

    const isItalic = slot.style === "italic" || slot.style === "italic_serif_center" || slot.style === "italic_serif";
    const isBold = slot.style === "bold";
    const isCenter = slot.style === "italic_serif_center";
    const isSerif = slot.style === "italic_serif_center" || slot.style === "italic_serif";
    const isSmall = slot.style === "small";

    let fontSize = Math.round((slot.font_size ?? 16) * s);
    if (!cardsMode && slot === slots[0] && isItalic) fontSize = Math.round(22 * s);
    if (cardsMode) fontSize = Math.round(14 * s);
    if (isBold) fontSize = Math.round(18 * s);
    if (isSmall) fontSize = Math.round(13 * s);
    if (slot.font_size != null) fontSize = Math.round(slot.font_size * s);

    const styleConfig = {
      fontStyle: slot.font_style || (isItalic ? "italic" : (isBold ? "bold" : "normal")),
      ...(slot.font_family || block.font_family ? { fontFamily: slot.font_family || block.font_family } : {}),
      ...(slot.align ? { align: slot.align } : {}),
      ...(slot.color || block.text_color ? {
        fill: themeColor(themeColors, slot.color || block.text_color, slot.color || block.text_color),
      } : {}),
      ...(slot.letter_spacing != null ? { letterSpacing: Math.round(slot.letter_spacing * s) } : {}),
      ...(slot.line_height != null ? { lineHeight: slot.line_height } : {}),
    };
    if (isSerif) styleConfig.fontFamily = "Georgia, serif";
    if (isCenter) styleConfig.align = "center";

    const valueStr = slot.text_transform === "uppercase" ? String(value).toUpperCase() : String(value);
    const hasMarkdown = /\*\*[^*]+\*\*|_[^_]+_/.test(valueStr);
    const maxLines = _measureOnly ? null : (slot.max_lines ?? block.max_lines);
    const slotGap = Math.round((slot.gap_after ?? 8) * s);

    if (cardsMode) {
      const cardPad = Math.round(14 * s);
      const accentW = Math.round(3 * s);
      const cardBg = themeColor(themeColors, block.card_background || "panel", block.card_background || "#F5F1E8");
      const accentColor = themeColor(themeColors, block.accent_bar?.color || "accent", block.accent_bar?.color || "#1F3A2E");

      const innerW = contentWidth - cardPad * 2 - accentW;
      const cardTextX = padding + accentW + cardPad;

      let cardText = new CustomText({
        x: cardTextX, y: cy + cardPad,
        width: innerW,
        text: valueStr, fontSize, lineHeight: 1.4,
        fill: themeColor(themeColors, "text", "#1a1a1a"),
        ...styleConfig,
        withMarkdown: hasMarkdown,
        _preview: isPreview,
        refreshCallback: imageCallback
      });
      if (maxLines && cardText.textArr && cardText.textArr.length > maxLines) {
        const charsPerLine = valueStr.length / Math.max(cardText.textArr.length, 1);
        const targetChars = Math.floor(charsPerLine * maxLines);
        const truncated = valueStr.slice(0, targetChars).replace(/\s+\S*$/, "") + "…";
        cardText.destroy();
        cardText = new CustomText({
          x: cardTextX, y: cy + cardPad,
          width: innerW,
          text: truncated, fontSize, lineHeight: 1.4,
          fill: themeColor(themeColors, "text", "#1a1a1a"),
          ...styleConfig,
          withMarkdown: /\*\*[^*]+\*\*|_[^_]+_/.test(truncated),
          _preview: isPreview,
          refreshCallback: imageCallback
        });
      }

      const cardH = cardText.height() + cardPad * 2;

      if (!_measureOnly && height && height < 99999 && cy + cardH > height) {
        cardText.destroy();
        break;
      }

      const cardBgRect = new Konva.Rect({
        x: padding, y: cy,
        width: contentWidth, height: cardH,
        fill: cardBg, cornerRadius: Math.round(2 * s)
      });
      const accentRect = new Konva.Rect({
        x: padding, y: cy,
        width: accentW, height: cardH,
        fill: accentColor
      });
      group.add(cardBgRect);
      group.add(accentRect);
      group.add(cardText);
      if (slot.key) tagEvidence(cardText, `${block.id}.${slot.key}`, options);
      cardRefs.push({
        bgRect: cardBgRect, accentRect, textNode: cardText,
        originalY: cy, originalH: cardH,
        textH: cardText.height(),
        cardPadTop: cardPad
      });
      cy += cardH + (si < slots.length - 1 ? slotGap : 0);
      continue;
    }

    // Standard (non-card) slot
    const slotText = makeSlotText(valueStr, fontSize, styleConfig, hasMarkdown, maxLines, isPreview);

    if (maxLines && slotText.textArr && slotText.textArr.length > maxLines) {
      const charsPerLine = valueStr.length / Math.max(slotText.textArr.length, 1);
      const targetChars = Math.floor(charsPerLine * maxLines);
      const truncated = valueStr.slice(0, targetChars).replace(/\s+\S*$/, "") + "…";
      const hasMd = /\*\*[^*]+\*\*|_[^_]+_/.test(truncated);
      slotText.destroy();
      const truncatedText = new CustomText({
        x: padding, y: cy, width: contentWidth,
        text: truncated, fontSize, lineHeight: 1.35,
        fill: themeColor(themeColors, "text", "#1a1a1a"),
        ...styleConfig,
        withMarkdown: hasMd,
        _preview: isPreview,
        refreshCallback: imageCallback
      });
      group.add(truncatedText);
      cy += truncatedText.height() + (si < slots.length - 1 ? slotGap : 0);
    } else {
      group.add(slotText);
      cy += slotText.height() + (si < slots.length - 1 ? slotGap : 0);
    }
  }

  // Card expansion: distribute leftover height to visible cards
  if (cardsMode && cardRefs.length > 0 && !_measureOnly && height && height < 99999) {
    const slack = height - cy - padBottom;
    if (slack > 0) {
      const naturalSum = cardRefs.reduce((s, r) => s + r.originalH, 0);
      let cumulativeOffset = 0;
      for (const ref of cardRefs) {
        const share = slack * (ref.originalH / naturalSum);
        const newY = ref.originalY + cumulativeOffset;
        const newH = ref.originalH + share;
        ref.bgRect.y(newY);
        ref.bgRect.height(newH);
        ref.accentRect.y(newY);
        ref.accentRect.height(newH);
        const textY = newY + Math.max(ref.cardPadTop, (newH - ref.textH) / 2);
        ref.textNode.y(textY);
        cumulativeOffset += share;
      }
      cy = height - padBottom;
    }
  }

  let finalHeight = cy + padBottom;
  if (block.fill_height && !_measureOnly && height && height < 99999 && height > finalHeight) {
    const align = block.vertical_align || "top";
    const offset = align === "middle" ? (height - finalHeight) / 2 : align === "bottom" ? height - finalHeight : 0;
    if (offset > 0) {
      for (const child of group.getChildren()) {
        if (child !== bgRect && child !== accentBarRect) child.y(child.y() + offset);
      }
    }
    finalHeight = height;
  }
  group.height(finalHeight);
  if (bgRect) bgRect.height(finalHeight - marginTop);
  if (accentBarRect) accentBarRect.height(finalHeight - marginTop);
  addDebugOverlay(group, block, { ...options, height: group.height() });
  return group;
}
